import InboxRuleClause from './InboxRuleClause';
import addNewClause from '../actions/addNewClause';
import getClauseSectionProperties from '../actions/helpers/getClauseSectionProperties';
import getSectionCompletionStatus from '../actions/helpers/getSectionCompletionStatus';
import hasUsedAllClauseTypesInSection from '../actions/helpers/hasUsedAllClauseTypesInSection';
import setClauseErrorText from '../actions/helpers/setClauseErrorText';
import setFocusedClauseId from '../actions/helpers/setFocusedClauseId';
import setRuleName from '../actions/setRuleName';
import { ClauseSectionProperties } from '../actions/types/ClauseSectionType';
import ClauseSectionType from '../actions/types/ClauseSectionType';
import ActionClauseType from '../store/schema/ActionClauseType';
import ConditionalExceptionClauseType from '../store/schema/ConditionalExceptionClauseType';
import InboxRuleSectionCompletionStatus from '../store/schema/InboxRuleSectionCompletionStatus';
import store from '../store/store';
import isValidClause from '../utils/isValidClause';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { ActionButton } from 'office-ui-fabric-react/lib/Button';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { TextField, ITextField } from 'office-ui-fabric-react/lib/TextField';
import { ControlIcons } from 'owa-control-icons';
import strings from 'owa-strings/owa-options-strings';
import * as React from 'react';

const styles = require('./InboxRulesSection.scss');
let classNames = require('classnames/bind').bind(styles);

export interface InboxRulesSectionProps {
    numStep: number;
    sectionType: ClauseSectionType;
    clauseIds?: string[];
    clauseTypeOptions?: ConditionalExceptionClauseType[] | ActionClauseType[];
}

@observer
export default class InboxRulesSection extends React.Component<InboxRulesSectionProps, {}> {
    private nameTextField: ITextField;

    componentDidMount() {
        if (this.props.sectionType === 'name' && this.nameTextField) {
            this.nameTextField.focus();
        }
    }

    @computed
    get sectionCompletionStatus(): InboxRuleSectionCompletionStatus {
        return getSectionCompletionStatus(this.props.sectionType, this.props.clauseIds);
    }

    private setNameTextFieldRef = (input: ITextField) => {
        this.nameTextField = input;
    };

    render() {
        let sectionProperties = getClauseSectionProperties(this.props.sectionType);
        let sectionStatus = this.sectionCompletionStatus;
        let sectionNumberClassName = classNames(
            styles.sectionNumber,
            sectionStatus == InboxRuleSectionCompletionStatus.InProgress &&
                styles.inProgressSection,
            sectionStatus == InboxRuleSectionCompletionStatus.Incomplete &&
                styles.incompleteSection,
            sectionStatus == InboxRuleSectionCompletionStatus.Complete && styles.completeSection
        );
        let showAddException =
            this.props.sectionType == 'action' &&
            store.inboxRulesEditState.exceptionClauseIds.length == 0;

        return (
            <div className={styles.sectionContainer}>
                <div className={sectionNumberClassName}>
                    {sectionStatus == InboxRuleSectionCompletionStatus.Complete ? (
                        <Icon
                            iconName={ControlIcons.CheckMark}
                            className={styles.sectionCompleteCheckmark}
                        />
                    ) : (
                        this.props.numStep
                    )}
                </div>
                <div className={styles.sectionDetails}>
                    {this.props.sectionType === 'name' ? (
                        <div className={styles.ruleNameContainer}>
                            <TextField
                                autoComplete="off"
                                onFocus={() => this.setFocusOnNameField(true)}
                                onBlur={this.validateName}
                                value={store.inboxRulesEditState.ruleName}
                                placeholder={strings.options_inboxRules_nameRulePlaceholder}
                                onChanged={this.onRuleNameInputChanged}
                                errorMessage={store.inboxRulesEditState.clauseErrorTextMap.get(
                                    'name'
                                )}
                                componentRef={this.setNameTextFieldRef}
                            />
                        </div>
                    ) : (
                        <div>
                            {this.renderSectionHeader(sectionProperties)}
                            {this.getInboxRuleSectionClauses(sectionProperties)}
                            {showAddException && (
                                <ActionButton
                                    className={styles.addAnExceptionButton}
                                    onClick={evt => {
                                        addNewClause('exception', null, null);
                                    }}
                                    text={strings.options_inboxRules_addExceptionSection}
                                />
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    private getInboxRuleSectionClauses(sectionProperties: ClauseSectionProperties): JSX.Element {
        let clauseIds = this.props.clauseIds;
        let clauseTypeOptions = this.props.clauseTypeOptions;
        let sectionType = this.props.sectionType;
        let inboxRuleClauses: JSX.Element[] = [];
        let lastClauseInSection = clauseIds[clauseIds.length - 1];

        for (let i = 0; i < clauseIds.length; i++) {
            let clauseId = clauseIds[i];
            inboxRuleClauses.push(
                <InboxRuleClause
                    key={clauseId}
                    clauseId={clauseId}
                    clauseTypeOptions={clauseTypeOptions}
                    sectionType={sectionType}
                    sectionClauseIds={clauseIds}
                />
            );
        }

        return (
            <div className={styles.inboxRuleClausesContainer}>
                {inboxRuleClauses}
                {this.shouldShowAddNewClause() && (
                    <ActionButton
                        className={styles.addClause}
                        onClick={evt => {
                            // validate the previous clause in the section
                            setClauseErrorText(
                                lastClauseInSection,
                                isValidClause(lastClauseInSection)
                            );
                            addNewClause(sectionType, null, null);
                        }}
                        text={sectionProperties.addClauseText}
                    />
                )}
            </div>
        );
    }

    private onRuleNameInputChanged(inputText: string) {
        setRuleName(inputText);
    }

    private shouldShowAddNewClause() {
        let clauseIds = this.props.clauseIds;
        let lastClauseInSection = store.inboxRulesEditState.clauseMap.get(
            clauseIds[clauseIds.length - 1]
        );

        // only show add clause button if
        // 1) the last clause in the section has a type
        // 2) user hasn't used all possible clause types yet
        return (
            lastClauseInSection &&
            lastClauseInSection.clauseType &&
            !hasUsedAllClauseTypesInSection(this.props.sectionType)
        );
    }

    private renderSectionHeader(sectionProperties: ClauseSectionProperties): JSX.Element {
        return (
            <div className={styles.sectionLabel}>
                <span>{sectionProperties.headerText}</span>
            </div>
        );
    }

    private setFocusOnNameField = (shouldSetFocus: boolean) => {
        setFocusedClauseId('name', shouldSetFocus);
    };

    private validateName = () => {
        setClauseErrorText('name', store.inboxRulesEditState.ruleName.trim().length > 0);
        setFocusedClauseId('name', false);
    };
}
